import { Component, OnInit, OnDestroy, ViewChild, TemplateRef } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { HeaderService } from '../../../../services/header/header.service';
import { ClientService } from '../../../../services/Client/client.service';
import { TresorerieService } from '../../../../services/tresorerie/tresorerie.service';
import { Client } from '../../../../models/client';
import * as Feather from 'feather-icons';
import { EmailComposeComponent } from '../../Vente/devis/parametre-bar/email-compose/email-compose.component';

@Component({
    selector: 'app-details',
    templateUrl: './details.component.html',
    styleUrls: ['./details.component.scss'],
})
export class DetailsComponent implements OnInit, OnDestroy {
    @ViewChild('cashAdvanceModal') cashAdvanceModal!: TemplateRef<any>;

    client: Client | null = null;
    isLoading: boolean = false;
    error: string | null = null;
    stationId: number = 0;
    userId: number = 0;
    cashAdvance: { montant: number | null; note: string } = { montant: null, note: '' };
    private subscriptions: Subscription[] = [];
    private navigationState: any = null;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private toastr: ToastrService,
        private clientService: ClientService,
        private tresorerieService: TresorerieService,
        private headerService: HeaderService,
        private modalService: NgbModal
    ) {
        this.subscriptions.push(
            this.router.events
                .pipe(filter((event) => event instanceof NavigationEnd))
                .subscribe(() => {
                    const navigation = this.router.getCurrentNavigation();
                    this.navigationState = navigation?.extras.state;
                    // console.log('DetailsComponent: Captured navigation state=', this.navigationState);
                })
        );
    }

    ngOnInit(): void {
        this.headerService.setConfig({
            title: 'Clients',
            buttons: [
                {
                    label: 'Avance en espèce',
                    type: 'primary',
                    action: () => this.handleCashAdvance(),
                },
                {
                    label: 'Modifier',
                    type: 'secondary',
                    action: () => this.editClient(this.client!),
                },
            ],
        });
        this.loadUserData();

        let clientId: string | null = this.route.snapshot.paramMap.get('reference');
        // console.log('DetailsComponent: Client ID from route=', clientId);
        // console.log('DetailsComponent: Route params=', Array.from(this.route.snapshot.paramMap.keys));

        if (!clientId && this.navigationState) {
            const state = this.navigationState as { client: Client } | undefined;
            clientId = state?.client?.id?.toString() || state?.client?.identifiant_unique || null;
            // console.log('DetailsComponent: Client ID from navigation state=', clientId, 'State=', state);
        }

        if (!clientId) {
            this.error = 'Aucun ID de client fourni dans la route ou l\'état de navigation.';
            this.toastr.error(this.error, 'Erreur');
            console.error('DetailsComponent: Route params=', Array.from(this.route.snapshot.paramMap.keys));
            this.router.navigate(['/bibliotheque-clients/actif']);
            return;
        }

        const parsedId = typeof clientId === 'string' && !isNaN(parseInt(clientId, 10)) ? parseInt(clientId, 10) : clientId;
        if (typeof parsedId === 'string' && !parsedId) {
            this.error = `ID de client invalide: ${clientId}`;
            this.toastr.error(this.error, 'Erreur');
            console.error('DetailsComponent: Invalid client ID=', clientId);
            this.router.navigate(['/bibliotheque-clients/actif']);
            return;
        }

        this.loadClientData(parsedId);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }

    private loadUserData(): void {
        try {
            const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
            this.stationId = user?.stations?.[0]?.id || 0;
            this.userId = user?.id || user?.user_id || 0;
            // console.log('DetailsComponent User Data:', user, 'Station ID:', this.stationId, 'User ID:', this.userId);
            if (!this.stationId) {
                this.error = 'Aucune station trouvée. Veuillez créer une station.';
                this.toastr.error(this.error, 'Erreur');
                this.router.navigate(['/create-station']);
            }
            if (!this.userId) {
                console.warn('Aucun user_id trouvé dans les données utilisateur');
            }
        } catch (error) {
            console.error('Error parsing currentUser from localStorage:', error);
            this.error = 'Erreur lors de la récupération des données utilisateur.';
            this.toastr.error(this.error, 'Erreur');
            this.stationId = 0;
            this.userId = 0;
        }
    }

    private loadClientData(clientId: number | string): void {
        this.isLoading = true;
        const sub = this.clientService.getClientById(clientId).subscribe({
            next: (clientData: Client) => {
                if (clientData.station_id !== this.stationId) {
                    this.error = 'Le client appartient à une station différente.';
                    this.toastr.error(this.error, 'Erreur');
                    this.router.navigate(['/bibliotheque-clients/actif']);
                    return;
                }
                this.client = clientData;
                this.headerService.setConfig({
                    title: `Détails du client: ${clientData.nom_prenom || '--'}`,
                    buttons: [
                        {
                            label: 'Avance en espèce',
                            type: 'primary',
                            action: () => this.handleCashAdvance(),
                        },
                        {
                            label: 'Modifier',
                            type: 'secondary',
                            action: () => this.editClient(clientData),
                        },
                    ],
                });
                this.isLoading = false;
                setTimeout(() => Feather.replace(), 0);
            },
            error: (error) => {
                this.isLoading = false;
                this.error = error.error?.errors?.map((err: any) => err.message).join(', ') || 'Erreur lors du chargement des données du client';
                this.toastr.error(this.error ?? undefined, 'Erreur');
                console.error('loadClientData: Full error=', error);
                this.router.navigate(['/bibliotheque-clients/actif']);
            },
        });
        this.subscriptions.push(sub);
    }

    editClient(client: Client) {
        this.router.navigate([`/tiers/clients/edit/${client.id}`], {
            state: { client, isEditMode: true },
        });
    }

    handleCashAdvance(): void {
        if (!this.client) {
            this.toastr.error('Aucun client sélectionné.', 'Erreur');
            return;
        }
        this.cashAdvance = { montant: null, note: '' }; // Reset form data
        this.modalService.open(this.cashAdvanceModal, { centered: true });
    }

    submitCashAdvance(): void {
        if (!this.client || !this.client.id) {
            this.toastr.error('Aucun client sélectionné.', 'Erreur');
            return;
        }

        if (!this.stationId) {
            this.toastr.error('Aucune station trouvée.', 'Erreur');
            return;
        }

        const uid = Number(this.userId) || 0;
        if (!uid) {
            this.toastr.error('Session utilisateur invalide (user_id). Reconnectez-vous.', 'Erreur');
            return;
        }

        // Vérifier qu'au moins le montant ou la note est fourni
        if ((!this.cashAdvance.montant || this.cashAdvance.montant <= 0) &&
            (!this.cashAdvance.note || this.cashAdvance.note.trim() === '')) {
            this.toastr.error('Veuillez fournir au moins un montant ou une note.', 'Erreur');
            return;
        }

        const soldeAvance = (this.cashAdvance.montant != null && this.cashAdvance.montant > 0)
            ? Number(this.cashAdvance.montant)
            : 0;
        const noteAvance = (this.cashAdvance.note && this.cashAdvance.note.trim() !== '')
            ? this.cashAdvance.note.trim()
            : null;

        this.isLoading = true;

        const sub = this.tresorerieService
            .createAvanceEspeceClient(this.client.id, this.stationId, {
                solde_avance: soldeAvance,
                note_avance: noteAvance ?? undefined,
                user_id: uid,
            })
            .subscribe({
            next: (response) => {
                console.log('✅ [DetailsComponent] Avance en espèce enregistrée:', response);
                const msg = soldeAvance > 0
                    ? `Avance en espèce de ${soldeAvance} TND enregistrée pour ${this.client?.nom_prenom}`
                    : `Note enregistrée pour ${this.client?.nom_prenom}`;
                this.toastr.success(msg, 'Succès');
                this.modalService.dismissAll();
                if (this.client?.id) {
                    this.loadClientData(this.client.id);
                }
                this.cashAdvance = { montant: null, note: '' };
                this.isLoading = false;
            },
            error: (error) => {
                console.error('❌ [DetailsComponent] Erreur avance en espèce:', error);
                const errorMessage =
                    error.error?.message ||
                    error.error?.error ||
                    error.message ||
                    'Erreur lors de l\'enregistrement de l\'avance en espèce';
                this.toastr.error(errorMessage, 'Erreur');
                this.isLoading = false;
            },
        });

        this.subscriptions.push(sub);
    }
}
